// Offer path of the composer's Offer/Points radio (docs/06-ux-rules.md
// "Composer"; v70 `.rp-search` / `.rp-menu` / `.rp-chip`). A combobox search
// whose pick swaps the field for a chip (icon + name + ×). The list is
// portalled and fixed to the viewport so the composer's scroll box and
// InboxLayout's `overflow: hidden` card can't clip it (v70 positionRewardMenu).
// context: opens on user focus/click/typing/ArrowDown, not on the
//   auto-focus from an Offer-radio switch (FR-51 v11.8, v70 setRewardType).
//   Closes on pick, Escape, an outside mousedown, or focus moving away.
//   `autoFocus` is true only for the render after the operator picks Offer.
import { Button } from '@thanx/uikit/button'
import { Input } from '@thanx/uikit/input'
import { Text } from '@thanx/uikit/text'
import { Delete } from 'baseui/icon'
import Gift from 'components/Icons/Gift'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useStyletron } from 'styletron-react'
import {
  computeRewardMenuPosition,
  RewardMenuPosition,
  rewardMenuStyle,
} from './rewardMenuPosition'
import type { AvailableOffer } from './useAvailableOffers'

const t = buildTranslate('feedback_inbox.composer')

export const OFFER_LISTBOX_ID = 'inbox-composer-offer-listbox'
const optionId = (id: string) => `inbox-composer-offer-option-${id}`

// Prototype's exact `.rp-menu` shadow (v83) — a literal rgba, same
// precedent as InboxLayout.tsx's INBOX_CARD_SHADOW.
const OFFER_MENU_SHADOW = '0 10px 26px rgba(0, 0, 0, 0.14)'

// Portalled to <body>; only needs to clear the inbox's own layers
// (filter bar 5, thread actions menu 10).
const OFFER_MENU_Z_INDEX = 20
const CHIP_REMOVE_ICON_SIZE_PX = 16
// 2px belize ring for keyboard focus (WCAG 2.4.7).
const FOCUS_RING_SHADOW = `0 0 0 2px ${palette.belizeHole}`

// baseui drives its own style fn (and focus ring) off `$isFocusVisible`, not
// a `:focus-visible` CSS pseudo-class — see baseui/button/styled-components.js.
type BaseButtonStyleProps = { $isFocusVisible: boolean }

// The chip's ×. `overrides.BaseButton.style` REPLACES uikit's own
// styling, so bg/hover/focus-ring are restated here.
// context: padding is longhand, not shorthand, so it can't lose an
//   injection-order race to baseui's own longhand padding declarations.
//   16px icon + 4px padding = 24px target. Exported because css() is a
//   no-op in this file's provider-less test, so the style object is the
//   only thing a test can pin.
export function chipRemoveButtonOverrides() {
  return {
    BaseButton: {
      style: ({ $isFocusVisible }: BaseButtonStyleProps) => ({
        display: 'flex',
        flexShrink: 0,
        paddingTop: '4px',
        paddingRight: '4px',
        paddingBottom: '4px',
        paddingLeft: '4px',
        minWidth: '0',
        width: 'auto',
        height: 'auto',
        borderTopLeftRadius: '4px',
        borderTopRightRadius: '4px',
        borderBottomRightRadius: '4px',
        borderBottomLeftRadius: '4px',
        backgroundColor: 'transparent',
        color: palette.grey70,
        boxShadow: $isFocusVisible ? FOCUS_RING_SHADOW : 'none',
        ':hover': { backgroundColor: 'transparent', color: palette.grey90 },
        ':active': { backgroundColor: 'transparent' },
      }),
    },
  }
}

export interface OfferPickerProps {
  offers: AvailableOffer[]
  isLoading: boolean
  isError: boolean
  selectedId: string | null
  onSelect: (offer: AvailableOffer) => void
  // Chip ×. InboxComposer passes `handleRemoveReward`, which also retracts
  // the Q7 reward-only prefill (never merchant-typed text).
  onClear: () => void
  disabled?: boolean
  // True only for the render where the operator has just switched the
  // reward-type radio to Offer (see `InboxComposer/index.tsx`) — never true
  // on this component's initial mount (Offer is the composer's default
  // radio) or on an item switch that happens to reset back to Offer
  // underneath the operator.
  autoFocus: boolean
}

const OfferPicker = ({
  offers,
  isLoading,
  isError,
  selectedId,
  onSelect,
  onClear,
  disabled,
  autoFocus,
}: OfferPickerProps) => {
  const [css] = useStyletron()
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState<RewardMenuPosition | null>(
    null
  )
  const inputRef = useRef<HTMLInputElement>(null)
  const searchWrapRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  // Set by the chip's ×: the search field only exists again next render.
  const refocusSearchRef = useRef(false)
  // Set for the duration of the autoFocus mount effect's `.focus()` call so
  // the resulting focus event doesn't also open the list (FR-51 v11.8).
  const suppressOpenOnFocusRef = useRef(false)

  const selected = offers.find(offer => offer.id === selectedId) ?? null
  const selectedOfferId = selected?.id ?? null
  const filtered = offers.filter(offer =>
    offer.name.toLowerCase().includes(query.toLowerCase())
  )

  const measure = useCallback(() => {
    const wrap = searchWrapRef.current
    if (!wrap) return
    setMenuPosition(
      computeRewardMenuPosition(
        wrap.getBoundingClientRect(),
        window.innerHeight
      )
    )
  }, [])

  const open = useCallback(() => {
    measure()
    setIsOpen(true)
  }, [measure])

  const isInsideControl = useCallback(
    (node: Node) =>
      !!searchWrapRef.current?.contains(node) ||
      !!menuRef.current?.contains(node),
    []
  )

  // Focuses the search field only when the mount itself was caused by the
  // operator picking the Offer radio (see the `autoFocus` prop doc above).
  // Suppresses the resulting focus event's open so the switch itself never
  // reveals the list (FR-51 v11.8, v70 `setRewardType`); the list opens on
  // the operator's own click/typing/ArrowDown instead.
  useEffect(() => {
    if (autoFocus) {
      suppressOpenOnFocusRef.current = true
      inputRef.current?.focus()
      suppressOpenOnFocusRef.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setActiveIndex(0)
  }, [query])

  // Refocuses the search once it remounts after the chip's × clears the
  // pick (v70 clearRewardChoice → filterRewards()).
  useEffect(() => {
    if (selectedOfferId !== null || !refocusSearchRef.current) return
    refocusSearchRef.current = false
    inputRef.current?.focus()
  }, [selectedOfferId])

  useEffect(() => {
    if (!isOpen) return
    const handleOutsideMouseDown = (event: MouseEvent) => {
      if (event.target instanceof Node && isInsideControl(event.target)) {
        return
      }
      setIsOpen(false)
    }
    // An ancestor scroll invalidates the measured anchor rect, and the
    // portalled `position:fixed` menu can't follow it out of the ancestor's
    // own clip region (the composer's `overflowY:auto`) without floating
    // disconnected from the input — so close instead of re-measuring.
    // Scrolling the list itself doesn't move the anchor.
    const handleScroll = (event: Event) => {
      if (!(event.target instanceof Node)) return
      if (menuRef.current?.contains(event.target)) return
      if (event.target.contains(searchWrapRef.current)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleOutsideMouseDown, true)
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', measure)
    return () => {
      document.removeEventListener('mousedown', handleOutsideMouseDown, true)
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', measure)
    }
  }, [isOpen, isInsideControl, measure])

  const pick = (offer: AvailableOffer) => {
    setQuery('')
    setIsOpen(false)
    onSelect(offer)
  }

  const handleClear = () => {
    refocusSearchRef.current = true
    onClear()
  }

  // Chosen state — the search field and list are replaced by a chip (v70
  // `.rp-chip`); the × clears the pick via `onClear` (InboxComposer wires
  // `handleRemoveReward`) and restores + refocuses the search.
  if (selected) {
    return (
      <div
        data-testid="inbox-composer-offer-chip"
        className={css({
          display: 'flex',
          alignItems: 'center',
          gap: '9px',
          border: `1px solid ${palette.belizeHole}`,
          borderRadius: '6px',
          padding: '10px 12px',
          backgroundColor: palette.belizeHole10,
        })}
      >
        <Gift
          fillColor={palette.belizeHole}
          className={css({ flexShrink: 0 })}
        />
        <span
          className={css({
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          })}
        >
          <Text.BodyText4 tag="span" bold color="grey90">
            {selected.name}
          </Text.BodyText4>
        </span>
        <Button
          kind="minimal"
          onClick={handleClear}
          disabled={disabled}
          aria-label={t('remove_offer_aria', { offer: selected.name })}
          overrides={chipRemoveButtonOverrides()}
        >
          <Delete size={CHIP_REMOVE_ICON_SIZE_PX} title="" aria-hidden="true" />
        </Button>
      </div>
    )
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      // Opening reveals the current activeIndex (0 by default) — don't also
      // advance it, or the first ArrowDown on a closed list skips offer 0.
      if (!isOpen) {
        open()
        return
      }
      setActiveIndex(index => Math.min(index + 1, filtered.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex(index => Math.max(index - 1, 0))
    } else if (event.key === 'Enter') {
      // The input stays focused after Escape (no blur) and after an
      // ancestor-scroll close, so `activeIndex` can still point at an offer
      // with nothing visibly open — without this guard, Enter would
      // silently attach it.
      if (!isOpen) return
      event.preventDefault()
      const offer = filtered[activeIndex]
      if (offer) pick(offer)
    } else if (event.key === 'Escape') {
      setQuery('')
      setIsOpen(false)
    }
  }

  // Tab-away / click on another field closes. A null relatedTarget
  // (scrollbar, window switch, non-focusable click) is left to the
  // outside-mousedown listener, so an inside click can never close the list.
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const next = event.relatedTarget
    if (next instanceof Node && !isInsideControl(next)) {
      setIsOpen(false)
    }
  }

  const activeOption = filtered[activeIndex]

  return (
    <div ref={searchWrapRef} data-testid="inbox-composer-offer-search">
      <Input
        inputRef={inputRef}
        value={query}
        onChange={value => {
          setQuery(value)
          open()
        }}
        onFocus={() => {
          if (!suppressOpenOnFocusRef.current) open()
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={t('search_rewards_placeholder')}
        aria-label={t('search_rewards_placeholder')}
        disabled={disabled}
        // `role`/`aria-expanded`/`onClick` aren't in @thanx/uikit's `Input`/
        // baseui `BaseInput` prop TYPE (a real type gap, not a runtime one —
        // all ARE forwarded to the real `<input>` at runtime) —
        // `overrides.Input.props` is baseui's own supported extension point
        // for exactly this (same precedent as `InboxListRow.tsx`'s
        // `overrides.BaseButton.props={{ role: 'option' }}`), rather than
        // reaching for `inputRef` + an imperative listener. `onClick` reopens
        // the list on a click into an already-focused field (autoFocus, or
        // after Escape) — onFocus alone won't refire in that case. The rest
        // of the combobox's aria-* live here too, for one consolidated place
        // describing this input's accessible shape.
        overrides={{
          Input: {
            props: {
              role: 'combobox',
              onClick: open,
              'aria-expanded': isOpen,
              'aria-haspopup': 'listbox',
              'aria-controls': OFFER_LISTBOX_ID,
              'aria-autocomplete': 'list',
              'aria-activedescendant':
                isOpen && activeOption ? optionId(activeOption.id) : undefined,
            },
          },
        }}
      />
      {isOpen &&
        menuPosition &&
        createPortal(
          <div
            ref={menuRef}
            id={OFFER_LISTBOX_ID}
            role="listbox"
            aria-label={t('search_rewards_placeholder')}
            data-placement={menuPosition.placement}
            // #1155 / FR-51: preventDefault on the WHOLE list (padding,
            // scrollbar, loading/empty rows), so no mousedown inside it ever
            // blurs the input ahead of a click's own handler.
            onMouseDown={event => event.preventDefault()}
            className={css({
              overflowY: 'auto',
              padding: '6px',
              backgroundColor: palette.white,
              border: `1px solid ${palette.grey20}`,
              borderRadius: '8px',
              boxShadow: OFFER_MENU_SHADOW,
              zIndex: OFFER_MENU_Z_INDEX,
            })}
            // Live geometry from getBoundingClientRect can't be a static
            // css({}): same inline-style exemption as TourTooltipCard.
            style={rewardMenuStyle(menuPosition)}
          >
            {isLoading && (
              <div className={css({ padding: '8px 12px' })}>
                <Text.BodyText4 color="grey70">
                  {t('rewards_loading')}
                </Text.BodyText4>
              </div>
            )}
            {!isLoading && isError && (
              <div className={css({ padding: '8px 12px' })}>
                <Text.BodyText4 color="negative">
                  {t('rewards_error')}
                </Text.BodyText4>
              </div>
            )}
            {!isLoading && !isError && filtered.length === 0 && (
              <div className={css({ padding: '8px 12px' })}>
                <Text.BodyText4 color="grey70">
                  {t('no_rewards_match')}
                </Text.BodyText4>
              </div>
            )}
            {!isLoading &&
              !isError &&
              filtered.map((offer, index) => (
                <div
                  key={offer.id}
                  id={optionId(offer.id)}
                  role="option"
                  aria-selected={index === activeIndex}
                  onClick={() => pick(offer)}
                  className={css({
                    padding: '8px 12px',
                    cursor: 'pointer',
                    backgroundColor:
                      index === activeIndex ? palette.grey10 : 'transparent',
                  })}
                >
                  {offer.name}
                </div>
              ))}
          </div>,
          document.body
        )}
    </div>
  )
}

export default OfferPicker
